import type { CampaignTarget, CampaignTargetsResponse } from "@adpilot/shared";
import {
  IconArrowDown,
  IconArrowsUpDown,
  IconArrowUp,
  IconDownload,
  IconMaximize,
  IconPlus,
} from "@tabler/icons-react";
import { Link } from "@tanstack/react-router";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { ColumnCustomizerDialog } from "@/components/campaign/ColumnCustomizerDialog";
import { ControlButton } from "@/components/patterns/control-button";
import { DataTable, DataTableFrame } from "@/components/patterns/DataTable";
import { DataTablePagination } from "@/components/patterns/DataTablePagination";
import { FilterSelect } from "@/components/patterns/filter-select";
import { SearchInput } from "@/components/patterns/SearchInput";
import { UnavailableControl } from "@/components/patterns/unavailable-control";
import { EmptyState } from "@/components/shared/empty-state";
import { ErrorState } from "@/components/shared/error-state";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogHeader, DialogPopup, DialogTitle } from "@/components/ui/dialog";
import { SegmentControl, SegmentControlItem } from "@/components/ui/segment-control";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import {
  aggregateGroups,
  downloadCsv,
  formatGroupMetric,
  type GroupColumn,
  groupMetric,
  isSkuColumn,
  type MetricSource,
  MONETARY_COLUMNS,
} from "../ad-groups/ad-groups-table-model";
import {
  exportTargetsCsv,
  formatTargetCell,
  isTargetMetricColumn,
  sortTargets,
  TARGET_COLUMNS,
  TARGET_FIXED_COLUMNS,
  TARGET_SUB_TABS,
  type TargetColumn,
  type TargetSortKey,
  type TargetSubTab,
  targetSubTab,
} from "./targets-table-model";

export interface CampaignTargetsTableProps {
  campaignId: string;
  data?: CampaignTargetsResponse;
  previousData?: CampaignTargetsResponse;
  isLoading: boolean;
  isFetching: boolean;
  isError: boolean;
  onRetry: () => void;
  comparison: boolean;
  onComparisonChange: (value: boolean) => void;
  comparisonLoading?: boolean;
  comparisonError?: boolean;
  onComparisonRetry?: () => void;
  renderAdGroupLink?: (adGroupId: string, name: string) => ReactNode;
}

type FixedColumn = (typeof TARGET_FIXED_COLUMNS)[number];
const ALL_COLUMNS: readonly (FixedColumn | TargetColumn)[] = [
  ...TARGET_FIXED_COLUMNS,
  ...TARGET_COLUMNS,
];
const STATUS_FILTERS = ["all", "enabled", "paused", "archived"] as const;
const MATCH_FILTERS = ["all", "broad", "exact", "phrase", "theme"] as const;
const PAGE_SIZES = [25, 50, 100] as const;
/** Fields the current contract cannot supply or sort meaningfully. */
const UNSORTABLE: readonly TargetSortKey[] = ["topOfSearch", "tags"];
const SWITCH_STICKY = "sticky left-0 z-20 w-16 min-w-16 max-w-16 border-r border-line";
const NAME_STICKY =
  "sticky left-16 z-20 min-w-56 border-r border-line shadow-[4px_0_8px_-7px_rgb(15_23_42_/_0.45)]";

export function CampaignTargetsTable(props: CampaignTargetsTableProps) {
  const { t, i18n } = useTranslation("campaign");
  const text = (key: string, options?: Record<string, unknown>) =>
    t(`detail.targetsList.${key}`, options);
  const shared = (key: string, options?: Record<string, unknown>) =>
    t(`detail.adGroupsList.${key}`, options);
  const [subTab, setSubTab] = useState<TargetSubTab>("keyword");
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState<(typeof STATUS_FILTERS)[number]>("all");
  const [match, setMatch] = useState<(typeof MATCH_FILTERS)[number]>("all");
  const [tag, setTag] = useState("all");
  const [page, setPage] = useState(1);
  const [size, setSize] = useState<number>(PAGE_SIZES[0]);
  const [sort, setSort] = useState<{ key: TargetSortKey; direction: "asc" | "desc" }>({
    key: "spend",
    direction: "desc",
  });
  const [columns, setColumns] = useState<readonly TargetColumn[]>(TARGET_COLUMNS);
  const [expanded, setExpanded] = useState(false);

  const all = props.data?.connected ? props.data.targets : [];
  const inTab = all.filter((row) => targetSubTab(row) === subTab);
  const tagOptions = [...new Set(inTab.flatMap((row) => row.tags.map((item) => item.tag)))].sort();
  const filtered = sortTargets(
    inTab.filter(
      (row) =>
        row.label.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()) &&
        (status === "all" || row.status === status) &&
        (match === "all" ||
          (match === "theme" ? row.targetType === "theme" : row.keywordMatchType === match)) &&
        (tag === "all" || row.tags.some((item) => item.tag === tag)),
    ),
    sort.key,
    sort.direction,
  );
  const pages = Math.max(1, Math.ceil(filtered.length / size));
  const currentPage = Math.min(page, pages);
  const visible = filtered.slice((currentPage - 1) * size, currentPage * size);
  const total = aggregateGroups(filtered);
  const currency = props.data?.currency ?? null;
  const previous = new Map(props.previousData?.targets.map((row) => [row.id, row]));
  const previousTotal = aggregateGroups(
    filtered.flatMap((row) =>
      previous.get(row.id) ? [previous.get(row.id) as CampaignTarget] : [],
    ),
  );
  const reset = () => setPage(1);
  const cellText = {
    bidDefault: text("bidDefault"),
    deliveryStatus: (raw: string) =>
      i18n.exists(`campaign:detail.targetsList.deliveryStatus.${raw}`)
        ? text(`deliveryStatus.${raw}`)
        : raw,
  };
  const label = (key: TargetSortKey | FixedColumn): string => {
    if (key === "active") return shared("active");
    if (key === "name") return text(`columns.name.${subTab}`);
    if (isTargetMetricColumn(key as TargetColumn)) return shared(`columns.${key}`);
    return text(`columns.${key}`);
  };
  const sortBy = (key: TargetSortKey) => {
    setSort({ key, direction: sort.key === key && sort.direction === "asc" ? "desc" : "asc" });
    setPage(1);
  };

  /** Metric cell with optional previous-period sub line; never compares money across currencies. */
  const metric = (row: MetricSource, key: GroupColumn, old?: MetricSource) => {
    const value = groupMetric(row, key);
    const currencyMatches = props.previousData?.currency === currency && currency !== null;
    const usableOld = old && (!MONETARY_COLUMNS.includes(key) || currencyMatches) ? old : undefined;
    const before = usableOld ? groupMetric(usableOld, key) : null;
    const delta =
      value !== null && before !== null && before !== 0
        ? (value - before) / Math.abs(before)
        : null;
    return (
      <>
        <span className="font-mono tabular-nums text-ink">
          {formatGroupMetric(value, key, currency, i18n.language)}
        </span>
        {props.comparison && (
          <div
            className="font-mono text-[10px] leading-3.5 tabular-nums text-muted-foreground"
            title={shared("previous")}
          >
            {props.comparisonLoading ? (
              shared("loading")
            ) : props.comparisonError ? (
              "—"
            ) : (
              <>
                {usableOld ? formatGroupMetric(before, key, currency, i18n.language) : "—"} ·{" "}
                {delta === null
                  ? "—"
                  : new Intl.NumberFormat(i18n.language, {
                      style: "percent",
                      maximumFractionDigits: 2,
                      signDisplay: "exceptZero",
                    }).format(delta)}
              </>
            )}
          </div>
        )}
      </>
    );
  };
  const cellTitle = (row: CampaignTarget, key: TargetColumn) =>
    key === "bid"
      ? text("writeUnavailable")
      : key === "topOfSearch"
        ? shared("reportUnavailable")
        : isTargetMetricColumn(key) && isSkuColumn(key) && row.hasMetrics && !row.skuBreakdown
          ? shared("skuBreakdownPending")
          : undefined;
  const cell = (row: CampaignTarget, key: TargetColumn) => {
    if (isTargetMetricColumn(key)) return metric(row, key, previous.get(row.id));
    if (key === "adGroup")
      return props.renderAdGroupLink && row.adGroupId ? (
        props.renderAdGroupLink(row.adGroupId, row.adGroup)
      ) : row.adGroupId ? (
        <Link
          to="/campaigns/$id/ad-groups/$adGroupId"
          params={{ id: props.campaignId, adGroupId: row.adGroupId }}
          className="text-ink"
        >
          {row.adGroup}
        </Link>
      ) : (
        row.adGroup
      );
    if (key === "tags")
      return row.tags.length ? (
        <span className="flex flex-wrap justify-end gap-1">
          {row.tags.map((item) => (
            <Badge key={item.tag} variant="neutral" size="compact" className="font-normal">
              {item.tag}
            </Badge>
          ))}
        </span>
      ) : (
        "—"
      );
    return (
      <span className={cn(key === "bid" && "font-mono tabular-nums text-ink")}>
        {formatTargetCell(row, key, currency, i18n.language, cellText)}
      </span>
    );
  };
  const exportCsv = () => {
    downloadCsv(
      `campaign-${props.campaignId}-targets-${subTab}.csv`,
      exportTargetsCsv(filtered, ["name", ...columns], label, currency, i18n.language, cellText),
    );
  };

  const sortHeader = (key: TargetSortKey, className?: string) => {
    const active = sort.key === key;
    const sortable = !UNSORTABLE.includes(key);
    return (
      <TableHead
        key={key}
        className={className}
        aria-sort={active ? (sort.direction === "asc" ? "ascending" : "descending") : "none"}
        title={key === "topOfSearch" ? shared("reportUnavailable") : undefined}
      >
        {sortable ? (
          <button
            type="button"
            aria-label={label(key)}
            className="flex w-full min-w-0 items-center gap-1 truncate text-left uppercase outline-none focus-visible:ring-2 focus-visible:ring-ring"
            onClick={() => sortBy(key)}
          >
            <span className="truncate">{label(key)}</span>
            {active && sort.direction === "asc" ? (
              <IconArrowUp aria-hidden="true" className="size-3 shrink-0" />
            ) : active ? (
              <IconArrowDown aria-hidden="true" className="size-3 shrink-0" />
            ) : (
              <IconArrowsUpDown aria-hidden="true" className="size-3 shrink-0 opacity-45" />
            )}
          </button>
        ) : (
          <span className="truncate">{label(key)}</span>
        )}
      </TableHead>
    );
  };
  const numeric = (key: TargetColumn) => isTargetMetricColumn(key) || key === "bid";

  const table = (
    <DataTable density="compact" stickyHeader aria-label={text("title")}>
      <TableHeader>
        <TableRow>
          <TableHead className={cn(SWITCH_STICKY, "z-30")}>{shared("active")}</TableHead>
          {sortHeader("name", cn(NAME_STICKY, "z-30"))}
          {columns.map((key) =>
            sortHeader(key, numeric(key) ? "text-right [&>button]:justify-end" : undefined),
          )}
        </TableRow>
      </TableHeader>
      <TableBody>
        {visible.map((row) => (
          <TableRow key={row.id} className="hover:bg-table-row-hover">
            <TableCell className={cn(SWITCH_STICKY, "bg-surface")} title={text("writeUnavailable")}>
              <Switch
                aria-label={`${shared("active")} ${row.label}`}
                checked={row.status === "enabled"}
                disabled
              />
            </TableCell>
            <TableCell className={cn(NAME_STICKY, "bg-surface")}>
              <span
                className="block max-w-72 truncate text-[11px] font-semibold leading-4 text-ink"
                title={row.label}
              >
                {row.label}
              </span>
              <span className="block text-[10px] leading-3.5 text-muted-foreground">
                {row.keywordMatchType
                  ? text(`matchTypes.${row.keywordMatchType}`)
                  : text(`types.${row.targetType}`)}
              </span>
            </TableCell>
            {columns.map((key) => (
              <TableCell
                key={key}
                className={cn(numeric(key) && "text-right", key === "tags" && "text-right")}
                title={cellTitle(row, key)}
              >
                {cell(row, key)}
              </TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
      {filtered.length > 1 && (
        <TableFooter className="bg-subtle text-[11px] font-medium">
          <TableRow className="hover:bg-subtle">
            <TableCell colSpan={2} className="sticky left-0 z-20 border-r border-line bg-subtle">
              <span title={!total.hasMetrics ? text("incompleteTotal") : undefined}>
                {shared("total")}
                {!total.hasMetrics && (
                  <span className="block max-w-72 whitespace-normal text-[10px] font-normal leading-3.5 text-muted-foreground">
                    {text("incompleteTotal")}
                  </span>
                )}
              </span>
            </TableCell>
            {columns.map((key) => (
              <TableCell
                key={key}
                className={cn(numeric(key) && "text-right")}
                title={
                  isTargetMetricColumn(key) &&
                  isSkuColumn(key) &&
                  total.hasMetrics &&
                  !total.skuBreakdown
                    ? shared("skuBreakdownPending")
                    : undefined
                }
              >
                {isTargetMetricColumn(key) ? metric(total, key, previousTotal) : ""}
              </TableCell>
            ))}
          </TableRow>
        </TableFooter>
      )}
    </DataTable>
  );

  const disabledControl = (labelText: string, reason: string) => (
    <UnavailableControl variant="outline" reason={reason}>
      {labelText}
    </UnavailableControl>
  );

  const toolbar = (
    <>
      <div className="flex flex-wrap items-center gap-2">
        {subTab !== "auto" && (
          <UnavailableControl reason={text("writeUnavailable")}>
            <IconPlus data-icon="inline-start" />
            {text(`create.${subTab}`)}
          </UnavailableControl>
        )}
        {disabledControl(text("bulk"), text("writeUnavailable"))}
        {disabledControl("ASIN", shared("productUnavailable"))}
        <FilterSelect
          ariaLabel={text(`filters.active.${subTab}`)}
          items={STATUS_FILTERS.map((value) => ({
            value,
            label: value === "all" ? shared("states.all") : shared(`states.${value}`),
          }))}
          value={status}
          onValueChange={(value) => {
            setStatus(value as (typeof STATUS_FILTERS)[number]);
            reset();
          }}
        />
        {disabledControl(text(`filters.delivery.${subTab}`), shared("reportUnavailable"))}
        {subTab === "keyword" && (
          <FilterSelect
            ariaLabel={text("filters.match")}
            items={MATCH_FILTERS.map((value) => ({
              value,
              label: value === "all" ? shared("states.all") : text(`matchTypes.${value}`),
            }))}
            value={match}
            onValueChange={(value) => {
              setMatch(value as (typeof MATCH_FILTERS)[number]);
              reset();
            }}
          />
        )}
        <FilterSelect
          ariaLabel={text(`filters.tags.${subTab}`)}
          items={[
            { value: "all", label: text(`filters.tags.${subTab}`) },
            ...tagOptions.map((value) => ({ value, label: value })),
          ]}
          showAppliedState
          value={tag}
          onValueChange={(value) => {
            setTag(value);
            reset();
          }}
        />
        {disabledControl(shared("conditions"), shared("conditionsUnavailable"))}
      </div>
      <div className="flex items-center gap-2">
        <SearchInput
          aria-label={shared("search")}
          placeholder={shared("search")}
          value={search}
          showIcon
          textSize="compact"
          className="w-[200px]"
          onChange={(event) => {
            setSearch(event.target.value);
            reset();
          }}
        />
        <label
          htmlFor={`targets-comparison-${props.campaignId}`}
          className="flex items-center gap-1.5 whitespace-nowrap text-[11px] text-muted-foreground"
        >
          {shared("comparison")}
          <Switch
            id={`targets-comparison-${props.campaignId}`}
            aria-label={shared("comparison")}
            checked={props.comparison}
            onCheckedChange={props.onComparisonChange}
          />
        </label>
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={shared("export")}
          title={shared("export")}
          disabled={filtered.length === 0 || props.isLoading || props.isError}
          onClick={exportCsv}
        >
          <IconDownload />
        </ControlButton>
        <ControlButton
          variant="outline"
          controlSize="icon-toolbar"
          aria-label={shared("fullscreen")}
          title={shared("fullscreen")}
          onClick={() => setExpanded(true)}
        >
          <IconMaximize />
        </ControlButton>
        <ColumnCustomizerDialog
          allColumns={ALL_COLUMNS}
          fixedColumns={TARGET_FIXED_COLUMNS}
          defaultColumns={TARGET_COLUMNS}
          visibleColumns={columns}
          label={(column) => label(column)}
          description={text("customColumnsDescription")}
          onApply={(next) =>
            setColumns(
              next.filter(
                (column): column is TargetColumn => column !== "active" && column !== "name",
              ),
            )
          }
        />
      </div>
    </>
  );

  const body = props.isLoading ? (
    <div className="flex flex-col gap-2 p-3.5" role="status" aria-label={shared("loading")}>
      {[1, 2, 3, 4].map((n) => (
        <Skeleton key={n} className="h-8 w-full" />
      ))}
    </div>
  ) : props.isError ? (
    <div className="p-3.5">
      <ErrorState
        message={text("loadFailed")}
        onRetry={props.onRetry}
        isRetrying={props.isFetching}
      />
    </div>
  ) : filtered.length === 0 ? (
    <div className="p-3.5">
      <EmptyState
        title={text("empty")}
        description={text(inTab.length ? "noMatches" : "emptyDescription")}
      />
    </div>
  ) : (
    table
  );

  return (
    <section
      aria-label={text("title")}
      className="flex flex-col gap-[14px]"
      aria-busy={props.isFetching}
    >
      <SegmentControl
        size="compact"
        aria-label={text("title")}
        value={subTab}
        onValueChange={(value) => {
          setSubTab(value);
          setMatch("all");
          setTag("all");
          reset();
        }}
      >
        {TARGET_SUB_TABS.map((value) => (
          <SegmentControlItem key={value} value={value}>
            {text(`subTabs.${value}`)}
            <span className="font-normal tabular-nums">
              {all.filter((row) => targetSubTab(row) === value).length}
            </span>
          </SegmentControlItem>
        ))}
      </SegmentControl>
      <DataTableFrame
        toolbar={toolbar}
        footer={
          <DataTablePagination
            page={currentPage}
            pageCount={pages}
            pageSize={size}
            pageSizeOptions={PAGE_SIZES}
            summary={shared("count", { count: filtered.length })}
            onPageChange={setPage}
            onPageSizeChange={(next) => {
              setSize(next);
              reset();
            }}
          />
        }
      >
        {props.comparison && props.comparisonError && (
          <div className="border-b border-line p-3.5">
            <ErrorState
              variant="inline"
              message={shared("comparisonFailed")}
              onRetry={props.onComparisonRetry}
              isRetrying={props.comparisonLoading}
            />
          </div>
        )}
        {body}
      </DataTableFrame>
      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogPopup className="w-[95vw] max-w-[95vw]">
          <DialogHeader>
            <DialogTitle>{text("title")}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[80vh] overflow-auto px-6 pb-6">{table}</div>
        </DialogPopup>
      </Dialog>
    </section>
  );
}
